import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

/**
 * Stick-to-bottom for the chat thread.
 *
 * The viewport follows content growth (streamed text, expanding tool calls,
 * artifacts) while the reader is "pinned". The pin is released only by the
 * reader scrolling *up* — never by programmatic scrolls, smooth-scroll
 * animations landing short because content grew underneath them, or content
 * simply getting taller — and it is restored as soon as they return to
 * (near) the bottom.
 *
 * While the assistant is streaming the release is stricter: scrolling up
 * only lets go once the reader has scrolled past their own latest message
 * (the one the reply answers). Above it they are reading history and we hold
 * still; below it they are inside the live reply and we keep following.
 */

/** Within this distance of the bottom counts as "at the bottom". */
export const AT_BOTTOM_THRESHOLD_PX = 48

export interface ScrollSample {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
  /** `scrollTop` from the previous scroll event — tells the scroll direction. */
  previousScrollTop: number
  /** The assistant is producing the reply at the end of the thread. */
  streaming: boolean
  /**
   * Top of the reader's latest own message, in scroll coordinates
   * (`null` when the thread has none).
   */
  anchorTop: number | null
}

export function isAtBottom(
  s: Pick<ScrollSample, 'scrollTop' | 'scrollHeight' | 'clientHeight'>,
  threshold = AT_BOTTOM_THRESHOLD_PX
): boolean {
  return s.scrollHeight - s.scrollTop - s.clientHeight < threshold
}

export interface ResizeSample {
  /** Viewport width now and at the previous resize. */
  width: number
  previousWidth: number
  /** Content height now and at the previous resize. */
  scrollHeight: number
  previousScrollHeight: number
}

/**
 * Did content arrive below an unpinned reader — i.e. should the jump pill
 * start bouncing?
 *
 * A width change means the thread was re-laid out (the workspace panel
 * opening or closing, the window resizing): the same text reflows into a
 * taller column without a word being added, so that doesn't count as new.
 */
export function hasNewContentBelow(s: ResizeSample): boolean {
  if (s.width !== s.previousWidth) return false
  return s.scrollHeight > s.previousScrollHeight
}

/** The pinned state after a scroll event, given the state before it. */
export function nextPinned(pinned: boolean, s: ScrollSample): boolean {
  if (isAtBottom(s)) return true
  const scrolledUp = s.scrollTop < s.previousScrollTop
  if (!scrolledUp) return pinned
  if (!s.streaming) return false
  // Streaming: hold position only once the reader is above their sent message.
  if (s.anchorTop === null) return false
  return s.scrollTop < s.anchorTop ? false : pinned
}

interface UseStickToBottomOptions {
  /** Element whose subtree contains the scroll viewport. */
  rootRef: RefObject<HTMLElement | null>
  /** CSS selector for the scrolling viewport inside `rootRef`. */
  viewportSelector: string
  /** The growing content inside the viewport. */
  contentRef: RefObject<HTMLElement | null>
  /** Selector for the reader's messages; the last one is the streaming anchor. */
  anchorSelector: string
  streaming: boolean
  /** Changing this (thread switch, content becoming ready) re-pins from scratch. */
  resetKey: unknown
}

export function useStickToBottom({
  rootRef,
  viewportSelector,
  contentRef,
  anchorSelector,
  streaming,
  resetKey,
}: UseStickToBottomOptions) {
  const pinnedRef = useRef(true)
  const [pinned, setPinned] = useState(true)
  /** Content grew while unpinned — the jump pill should draw attention. */
  const [newBelow, setNewBelow] = useState(false)
  const streamingRef = useRef(streaming)
  streamingRef.current = streaming

  const getViewport = useCallback(
    () => rootRef.current?.querySelector<HTMLElement>(viewportSelector) ?? null,
    [rootRef, viewportSelector]
  )

  const setPinnedState = useCallback((next: boolean) => {
    if (next === pinnedRef.current) return
    pinnedRef.current = next
    setPinned(next)
  }, [])

  useEffect(() => {
    const viewport = getViewport()
    const content = contentRef.current
    if (!viewport || !content) return
    pinnedRef.current = true
    setPinned(true)
    setNewBelow(false)
    let previousScrollTop = viewport.scrollTop

    const anchorTop = (): number | null => {
      const anchors = content.querySelectorAll<HTMLElement>(anchorSelector)
      const last = anchors[anchors.length - 1]
      if (!last) return null
      return (
        last.getBoundingClientRect().top - viewport.getBoundingClientRect().top + viewport.scrollTop
      )
    }

    const onScroll = () => {
      const next = nextPinned(pinnedRef.current, {
        scrollTop: viewport.scrollTop,
        scrollHeight: viewport.scrollHeight,
        clientHeight: viewport.clientHeight,
        previousScrollTop,
        streaming: streamingRef.current,
        anchorTop: anchorTop(),
      })
      previousScrollTop = viewport.scrollTop
      setPinnedState(next)
      if (next) setNewBelow(false)
    }

    let previousWidth = viewport.clientWidth
    let previousScrollHeight = viewport.scrollHeight

    const follow = () => {
      const sample = {
        width: viewport.clientWidth,
        previousWidth,
        scrollHeight: viewport.scrollHeight,
        previousScrollHeight,
      }
      previousWidth = sample.width
      previousScrollHeight = sample.scrollHeight
      if (pinnedRef.current) {
        viewport.scrollTop = viewport.scrollHeight
        previousScrollTop = viewport.scrollTop
      } else if (hasNewContentBelow(sample)) {
        setNewBelow(true)
      }
    }

    viewport.addEventListener('scroll', onScroll, { passive: true })
    const observer = new ResizeObserver(follow)
    observer.observe(content)
    // The viewport itself shrinks when the on-screen keyboard opens.
    observer.observe(viewport)
    follow()
    return () => {
      viewport.removeEventListener('scroll', onScroll)
      observer.disconnect()
    }
  }, [resetKey, getViewport, contentRef, anchorSelector, setPinnedState])

  const scrollToBottom = useCallback(() => {
    pinnedRef.current = true
    setPinned(true)
    setNewBelow(false)
    const viewport = getViewport()
    if (viewport) viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' })
  }, [getViewport])

  return { pinned, newBelow, scrollToBottom }
}
